/**
 * Dry brewing yeast — Fermentis, Lallemand and Mangrove Jack's.
 * CellarScience ships in its own file.
 *
 * Fermentis' cider and souring lines are here too, under the Fermentis
 * heading: SafCider is typed `ale` with cider tags, the way every wine and
 * cider strain in this catalog is, and SafSour — like Lallemand's WildBrew
 * bacteria at the end of the LalBrew block — is typed `wild` with a nominal
 * attenuation band, following `escarpment-lactobacillus-blend-2`. A pure
 * bacteria pitch makes acid, not alcohol, so its attenuation figure describes
 * nothing a brewer should plan a gravity around, and each of those
 * descriptions says as much.
 *
 * Temperatures are °C. The labs publish their working ranges in °F or °C
 * depending on the market, so anything quoted in °F is converted and rounded to
 * whole degrees — a 64–75 °F sheet becomes 18–24 °C here, not 17.8–23.9.
 * Attenuation is stored as a band even where a lab prints a single headline
 * figure, because the published number assumes their pitch rate and wort.
 *
 * Two gaps are marked rather than papered over. Fermentis publishes attenuation
 * and sedimentation for its beer strains but for neither the SafCider range nor
 * the SafSour mixed cultures, so those entries are `representative` and each
 * description names the figure that was missing. And lallemandbrewing.com sits
 * behind a bot challenge that curl, WebFetch, r.jina.ai and headless Chrome all
 * failed, so the five entries added most recently under Lallemand — House Ale,
 * Aurora, LoNa, WildBrew Sour Pitch and WildBrew Helveticus Pitch — carry no
 * `url`. Their figures come off Lallemand's own technical data sheets, the same
 * PDFs Lallemand's e-shop links to, which were readable when the site was not.
 */

import type { Yeast } from '../../schema/yeast';

/** Dry yeast strains. @see Yeast */
export const dryYeasts: readonly Yeast[] = [
  // -------------------------------------------------------------------------
  // Fermentis (SafAle / SafLager)
  // -------------------------------------------------------------------------
  {
    id: 'fermentis-us-05',
    lab: 'Fermentis',
    productCode: 'US-05',
    name: 'SafAle US-05',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 78, max: 82 },
    temperatureC: { min: 15, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The default American ale yeast: clean, forgiving and almost flavor-free, so hops and malt do all the talking. Held below 20 °C it finishes crisp; pushed warm it throws a soft peach note that suits hazy pales more than lagers.',
    tags: ['neutral', 'american-ale', 'ipa', 'pale-ale', 'workhorse'],
    url: 'https://fermentis.com/en/product/safale-us-05/',
    dosageGramsPerHl: { min: 50, max: 80 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-s-04',
    lab: 'Fermentis',
    productCode: 'S-04',
    name: 'SafAle S-04',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 15, max: 24 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Fast, hard-flocculating English ale strain that drops bright in days and leaves a touch of residual sweetness. Ferment it at the cool end for a restrained bitter; above 21 °C it gets distinctly fruity and can stall early if underpitched.',
    tags: ['english-ale', 'bitter', 'porter', 'fast', 'clearing'],
    url: 'https://fermentis.com/en/product/safale-s-04/',
    dosageGramsPerHl: { min: 50, max: 80 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-s-33',
    lab: 'Fermentis',
    productCode: 'S-33',
    name: 'SafAle S-33',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 68, max: 72 },
    temperatureC: { min: 15, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A deliberately low attenuator that leaves body and a rounded fruity sweetness behind. Useful when a mild, sweet stout or a low-alcohol ale needs mouthfeel it cannot get from gravity alone.',
    tags: ['english-ale', 'full-bodied', 'mild', 'sweet-stout', 'low-attenuation'],
    url: 'https://fermentis.com/en/product/safale-s-33/',
    dosageGramsPerHl: { min: 50, max: 80 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-t-58',
    lab: 'Fermentis',
    productCode: 'T-58',
    name: 'SafAle T-58',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 15, max: 24 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Spicy, peppery Belgian-leaning strain with a pronounced clove-and-pepper phenolic edge and an estery lift on top. Traditionally a bottle-conditioning yeast, and it still throws a firm sediment that carbonates reliably in package.',
    tags: ['belgian', 'phenolic', 'spicy', 'bottle-conditioning', 'estery'],
    url: 'https://fermentis.com/en/product/safale-t-58/',
    dosageGramsPerHl: { min: 50, max: 80 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-be-256',
    lab: 'Fermentis',
    productCode: 'BE-256',
    name: 'SafAle BE-256',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 82, max: 86 },
    temperatureC: { min: 15, max: 24 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Abbey-style strain built for strong Belgian ales: attenuates hard, tolerates gravity and finishes drier than the malt bill suggests. Character is more warm fruit and alcohol than clove, so it suits dubbels and quads better than witbiers.',
    tags: ['belgian', 'abbey', 'dubbel', 'high-gravity', 'estery'],
    url: 'https://fermentis.com/en/product/safale-be-256/',
    dosageGramsPerHl: { min: 50, max: 80 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-be-134',
    lab: 'Fermentis',
    productCode: 'BE-134',
    name: 'SafAle BE-134',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 89, max: 93 },
    temperatureC: { min: 18, max: 28 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'A saison strain sold as Saccharomyces cerevisiae var. diastaticus: it chews through dextrins and routinely finishes above 90 percent apparent attenuation. Bone dry, peppery and citric, but genuinely diastatic, so keep it away from shared equipment and mind package pressure.',
    tags: ['saison', 'diastatic', 'dry-finish', 'peppery', 'farmhouse'],
    url: 'https://fermentis.com/en/product/safale-be-134/',
    dosageGramsPerHl: { min: 50, max: 80 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-wb-06',
    lab: 'Fermentis',
    productCode: 'WB-06',
    name: 'SafAle WB-06',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 84, max: 88 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Wheat beer strain giving the clove-forward end of the weizen spectrum with modest banana. Stays in suspension for weeks, which is exactly what a hefeweizen wants and a problem for anything you need bright.',
    tags: ['wheat', 'hefeweizen', 'phenolic', 'clove', 'hazy'],
    url: 'https://fermentis.com/en/product/safale-wb-06/',
    dosageGramsPerHl: { min: 50, max: 80 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-k-97',
    lab: 'Fermentis',
    productCode: 'K-97',
    name: 'SafAle K-97',
    form: 'dry',
    type: 'hybrid',
    attenuationPercent: { min: 80, max: 84 },
    temperatureC: { min: 15, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Kölsch-type strain that ferments cool and clean with a faint fruity lift, then needs cold conditioning to clear. Famous for a huge rocky krausen, so leave real headspace or fit a blow-off.',
    tags: ['kolsch', 'hybrid', 'clean', 'altbier', 'big-krausen'],
    url: 'https://fermentis.com/en/product/safale-k-97/',
    dosageGramsPerHl: { min: 50, max: 80 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-w-34-70',
    lab: 'Fermentis',
    productCode: 'W-34/70',
    name: 'SafLager W-34/70',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 80, max: 84 },
    temperatureC: { min: 12, max: 18 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Weihenstephan 34/70 workhorse in dry form and the safe answer for almost any lager. Balanced, faintly sulfury while fermenting, and clean enough at 15 °C that a long cold lagering is optional rather than mandatory.',
    tags: ['lager', 'pilsner', 'helles', 'clean', 'workhorse'],
    url: 'https://fermentis.com/en/product/saflager-w-34-70/',
    dosageGramsPerHl: { min: 80, max: 120 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-s-189',
    lab: 'Fermentis',
    productCode: 'S-189',
    name: 'SafLager S-189',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 80, max: 84 },
    temperatureC: { min: 12, max: 18 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Swiss-origin lager strain with a rounder, softer malt profile than 34/70 and very little sulfur. A good pick for helles and bocks where you want the malt to read sweet rather than crisp.',
    tags: ['lager', 'helles', 'bock', 'malty', 'clean'],
    url: 'https://fermentis.com/en/product/saflager-s-189/',
    dosageGramsPerHl: { min: 80, max: 120 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-s-23',
    lab: 'Fermentis',
    productCode: 'S-23',
    name: 'SafLager S-23',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 78, max: 82 },
    temperatureC: { min: 12, max: 18 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A fruitier Bavarian-type lager yeast that leaves a pear-and-berry ester note rather than fermenting perfectly neutral. Ferment it at the bottom of its range and lager it properly or that fruit stays in the glass.',
    tags: ['lager', 'fruity', 'european-lager', 'bock', 'estery'],
    url: 'https://fermentis.com/en/product/saflager-s-23/',
    dosageGramsPerHl: { min: 80, max: 120 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-ha-18',
    lab: 'Fermentis',
    productCode: 'HA-18',
    name: 'SafBrew HA-18',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 98, max: 100 },
    temperatureC: { min: 25, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A yeast-plus-enzyme blend for very high-gravity beers: barleywine, imperial stout and barrel-aged strong ales that a normal strain would quit on. The sheet quotes apparent attenuation past 100 percent because the enzymes keep feeding it dextrins; we cap the stored band at 100. Treat package stability as a real concern.',
    tags: ['high-gravity', 'barleywine', 'imperial-stout', 'enzymes', 'thermotolerant'],
    url: 'https://fermentis.com/en/product/safbrew-ha-18/',
    dosageGramsPerHl: { min: 50, max: 80 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-w-68',
    lab: 'Fermentis',
    productCode: 'W-68',
    name: 'SafAle W-68',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 26 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The German weissbier profile in a dry sachet, and widely taken for the Weihenstephan 68 strain though Fermentis says only that it is a historical wheat-beer yeast: banana and clove together, the balance shifting toward banana as it warms. POF+, so it makes the phenols on purpose rather than as a fault. It drops powdery rather than clumping, which suits a beer meant to be poured hazy.',
    tags: ['weissbier', 'hefeweizen', 'banana', 'clove', 'pof-positive', 'german'],
    url: 'https://fermentis.com/en/product/safale-w-68/',
    dosageGramsPerHl: { min: 50, max: 80 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-bw-20',
    lab: 'Fermentis',
    productCode: 'BW-20',
    name: 'SafAle BW-20',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 77, max: 83 },
    temperatureC: { min: 18, max: 26 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Built for Belgian witbier rather than adapted to it: fruity and restrained where a weissbier strain is loud, with pepper and clove instead of banana. The low flocculation is the point — it stays in suspension and keeps the white haze a wit is supposed to have.',
    tags: ['witbier', 'belgian', 'wheat', 'peppery', 'clove', 'pof-positive'],
    url: 'https://fermentis.com/en/product/safale-bw-20/',
    dosageGramsPerHl: { min: 50, max: 80 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-e-30',
    lab: 'Fermentis',
    productCode: 'E-30',
    name: 'SafLager E-30',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 80, max: 84 },
    temperatureC: { min: 12, max: 18 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A lager strain chosen for esters rather than against them: Fermentis rates its total ester production high, which is the opposite of what a Pilsner strain is usually picked for. Useful where a lager is meant to taste of fruit — modern pale lagers, fruited styles — and wrong where it is meant to taste of nothing.',
    tags: ['lager', 'estery', 'fruity', 'modern-lager', 'cold-fermentation'],
    url: 'https://fermentis.com/en/product/saflager-e-30/',
    dosageGramsPerHl: { min: 80, max: 120 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-sh-45',
    lab: 'Fermentis',
    productCode: 'SH-45',
    name: 'SafLager SH-45',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 77, max: 82 },
    temperatureC: { min: 12, max: 18 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A lager strain selected to release and esterify thiols, so the citrus and tropical aroma bound up in the hops arrives in the glass instead of staying in the bine. It is a hop-forward lager tool: pair it with a thiol-rich hop and it pays, pair it with a noble hop and you have bought nothing.',
    tags: ['lager', 'thiol', 'hop-forward', 'tropical', 'citrus', 'cold-ipa'],
    url: 'https://fermentis.com/en/product/saflager-sh-45/',
    dosageGramsPerHl: { min: 80, max: 120 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-br-8',
    lab: 'Fermentis',
    productCode: 'BR-8',
    name: 'SafBrew BR-8',
    form: 'dry',
    type: 'wild',
    attenuationPercent: { min: 80, max: 95 },
    temperatureC: { min: 15, max: 25 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Dry Brettanomyces bruxellensis for the second fermentation, not the first: Fermentis sells it for bottle and cask conditioning, where it works through what the primary strain left and adds the funk over months. Fermentis publishes the temperature band but no attenuation or flocculation figure, so those two are typical values for the species rather than the maker\u2019s.',
    tags: ['brettanomyces', 'bottle-conditioning', 'secondary', 'funk', 'saison', 'lambic'],
    url: 'https://fermentis.com/en/product/safbrew-br-8/',
    dosageGramsPerHl: { min: 50, max: 80 },
    provenance: 'representative',
  },
  {
    id: 'fermentis-f-2',
    lab: 'Fermentis',
    productCode: 'F-2',
    name: 'SafAle F-2',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 15, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      "Sold for the second fermentation only: it takes glucose, sucrose and maltose but almost no maltotriose, so it eats the priming sugar and leaves the base beer's character alone. Fermentis rates it for beers under 10 % ABV and publishes neither an attenuation nor a flocculation figure, so the band here describes priming sugar rather than wort, and the settling grade follows the sheet's note that it sticks to the bottom yet rouses into a haze.",
    tags: ['bottle-conditioning', 'cask', 'refermentation', 'neutral', 'maltotriose-negative'],
    url: 'https://fermentis.com/en/product/safale-f-2/',
    dosageGramsPerHl: { min: 2, max: 35 },
    provenance: 'representative',
  },
  {
    id: 'fermentis-da-16',
    lab: 'Fermentis',
    productCode: 'DA-16',
    name: 'SafBrew DA-16',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 98, max: 100 },
    temperatureC: { min: 20, max: 32 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: null,
    description:
      'A yeast-and-glucoamylase blend for beers meant to finish bone dry, Brut IPA above all, and for worts strong enough to reach 16 % ABV. The enzyme keeps working on dextrins, so Fermentis prints apparent attenuation of 98–102 %; the band stored here stops at 100. Esters and higher alcohols are both rated high, and package stability is a real concern with any enzyme blend.',
    tags: ['brut-ipa', 'dry', 'enzymes', 'high-gravity', 'hop-forward', 'estery'],
    url: 'https://fermentis.com/en/product/safbrew-da-16/',
    dosageGramsPerHl: { min: 100, max: 160 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-dw-17',
    lab: 'Fermentis',
    productCode: 'DW-17',
    name: 'SafBrew DW-17',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 98, max: 100 },
    temperatureC: { min: 20, max: 32 },
    flocculation: 'medium',
    alcoholTolerancePercent: 17,
    sta1: null,
    description:
      'The same yeast-plus-enzyme idea aimed at complexity rather than neutrality: a POF-positive strain that finishes very dry and leaves fruity, woody and faintly sour notes, and will carry a wort to 17 % ABV. Fermentis prints 98–102 % apparent attenuation; the band here stops at 100.',
    tags: ['high-gravity', 'dry', 'enzymes', 'pof-positive', 'woody', 'estery'],
    url: 'https://fermentis.com/en/product/safbrew-dw-17/',
    dosageGramsPerHl: { min: 100, max: 160 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-ld-20',
    lab: 'Fermentis',
    productCode: 'LD-20',
    name: 'SafBrew LD-20',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 98, max: 100 },
    temperatureC: { min: 12, max: 20 },
    flocculation: 'high',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A lager strain packed with glucoamylase to strip the dextrins out of a light lager: the same alcohol as the yeast alone, up to a fifth less carbohydrate, and a flavor Fermentis keeps deliberately plain. Sedimentation is rated fast. Apparent attenuation is printed at 98–102 %; the band here stops at 100.',
    tags: ['lager', 'low-carb', 'dry', 'enzymes', 'neutral', 'cold-fermentation'],
    url: 'https://fermentis.com/en/product/safbrew-ld-20/',
    dosageGramsPerHl: { min: 160, max: 240 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-la-01',
    lab: 'Fermentis',
    productCode: 'LA-01',
    name: 'SafBrew LA-01',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 13, max: 17 },
    temperatureC: { min: 15, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A maltose- and maltotriose-negative Saccharomyces cerevisiae var. chevalieri for beers under 0.5 % ABV: it works through the simple sugars, leaves everything else and keeps the aroma subtle. Fermentis prints a single attenuation figure of 15 %, so the band here is the two points either side of it. POF-positive, and it settles powdery rather than in clumps.',
    tags: ['non-alcoholic', 'low-alcohol', 'maltose-negative', 'pof-positive', 'session'],
    url: 'https://fermentis.com/en/product/safbrew-la-01/',
    dosageGramsPerHl: { min: 50, max: 80 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-la-02',
    lab: 'Fermentis',
    productCode: 'LA-02',
    name: 'SafBrew LA-02',
    form: 'dry',
    type: 'wild',
    attenuationPercent: { min: 5, max: 10 },
    temperatureC: { min: 20, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      "Fermentis' first non-Saccharomyces selection: a Torulaspora delbrueckii for non-alcoholic beer that takes glucose and fructose, works slowly through sucrose and leaves maltose, maltotriose and dextrins untouched. It stops at 5–10 % apparent attenuation and is POF-negative, so none of the clove note the usual low-alcohol strains bring.",
    tags: ['non-alcoholic', 'low-alcohol', 'torulaspora', 'maltose-negative', 'pof-negative'],
    url: 'https://fermentis.com/en/product/safbrew-la-02/',
    dosageGramsPerHl: { min: 50, max: 80 },
    provenance: 'spec-sheet',
  },
  {
    id: 'fermentis-ab-1',
    lab: 'Fermentis',
    productCode: 'AB-1',
    name: 'SafCider AB-1',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 10, max: 30 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A cider strain picked for balance rather than volume: fresh apple and applesauce notes, low acidity, low ester production and a very good appetite for fructose, plus up to 1.5 g/L of malic acid consumed. Fermentis publishes no attenuation figure for the SafCider range, so the band here is what its rating for dry cider implies. Killer-sensitive but settles hard all the same.',
    tags: ['cider', 'perry', 'apple', 'balanced', 'malic-acid', 'dry-cider'],
    url: 'https://fermentis.com/en/product/safcider-ab-1/',
    dosageGramsPerHl: { min: 20, max: 30 },
    provenance: 'representative',
  },
  {
    id: 'fermentis-ac-4',
    lab: 'Fermentis',
    productCode: 'AC-4',
    name: 'SafCider AC-4',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 10, max: 30 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The fast, crisp one: a killer-positive cider yeast with intense fresh apple and floral aroma, high total acidity, high ethyl ester production and almost no malic acid consumed, so the juice keeps its bite. Rated for dry cider, and since Fermentis publishes no attenuation figure the band here is what a cider taken to dryness implies.',
    tags: ['cider', 'perry', 'apple', 'floral', 'crisp', 'killer', 'dry-cider'],
    url: 'https://fermentis.com/en/product/safcider-ac-4/',
    dosageGramsPerHl: { min: 10, max: 20 },
    provenance: 'representative',
  },
  {
    id: 'fermentis-as-2',
    lab: 'Fermentis',
    productCode: 'AS-2',
    name: 'SafCider AS-2',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 10, max: 30 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The rounder SafCider: apple and citrus over a sweet, full mouthfeel, medium acidity and a balanced mix of acetate and ethyl esters, with low acetaldehyde and low sulfur production. It clears well for a killer-sensitive strain. Rated for dry cider, and the attenuation band here is what that implies rather than a published figure.',
    tags: ['cider', 'perry', 'apple', 'citrus', 'sweet', 'full-body', 'dry-cider'],
    url: 'https://fermentis.com/en/product/safcider-as-2/',
    dosageGramsPerHl: { min: 10, max: 20 },
    provenance: 'representative',
  },
  {
    id: 'fermentis-tf-6',
    lab: 'Fermentis',
    productCode: 'TF-6',
    name: 'SafCider TF-6',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 15, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The fruit-forward one: a very high isoamyl acetate producer that pushes banana, pear and candy notes alongside apple and finishes sweet and round. Fermentis rates it for sweet cider and explicitly not for dry, so the attenuation band here sits below the dry finishers; no figure and no settling grade are published. It is nitrogen-hungry and slow, and its ideal window is 15–25 °C inside a 10–30 °C spectrum.',
    tags: ['cider', 'perry', 'banana', 'pear', 'sweet', 'estery', 'high-gravity'],
    url: 'https://fermentis.com/en/product/safcider-tf-6/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },
  {
    id: 'fermentis-lp-652',
    lab: 'Fermentis',
    productCode: 'LP 652',
    name: 'SafSour LP 652',
    form: 'dry',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 34, max: 40 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A homofermentative Lactiplantibacillus plantarum for kettle souring: pitched into unhopped wort at about 37 °C it drops pH to 3.2–3.6 within a day or two and leaves tropical, citrus fruitiness behind. It makes lactic acid, not alcohol, so a brewing yeast still has to ferment the beer afterwards, and the attenuation figure is nominal. Iso-alpha acids halve its growth at about 5 ppm, so sour before you hop.',
    tags: ['sour', 'lactobacillus', 'kettle-sour', 'gose', 'berliner-weisse', 'tropical'],
    url: 'https://fermentis.com/en/product/saf-sour-lp-652/',
    dosageGramsPerHl: { min: 10, max: 10 },
    provenance: 'representative',
  },
  {
    id: 'fermentis-lb-1',
    lab: 'Fermentis',
    productCode: 'LB 1',
    name: 'SafSour LB 1',
    form: 'dry',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 27, max: 37 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A heterofermentative Levilactobacillus brevis for kettle souring, chosen for a neutral aroma so that the yeast pitched after it still sets the character. Around 32 °C in unhopped wort it reaches pH 3.6–3.9 in 30 to 48 hours, producing lactic and a little acetic acid rather than alcohol; the attenuation figure is nominal. Growth halves at about 10 ppm iso-alpha acid.',
    tags: ['sour', 'lactobacillus', 'kettle-sour', 'gose', 'berliner-weisse', 'neutral'],
    url: 'https://fermentis.com/en/product/safsour-lb-1/',
    dosageGramsPerHl: { min: 10, max: 10 },
    provenance: 'representative',
  },
  // Barrel Instinct and Fruit Intuition dropped out of Fermentis's product
  // sitemap and their pages 404 as of October 2026, so they carry no link.
  {
    id: 'fermentis-barrel-instinct',
    lab: 'Fermentis',
    productCode: 'Barrel Instinct',
    name: 'SafSour Barrel Instinct',
    form: 'dry',
    type: 'wild',
    attenuationPercent: { min: 90, max: 100 },
    temperatureC: { min: 15, max: 25 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A whole mixed fermentation in one sachet: ale yeast, Brettanomyces bruxellensis, three souring bacteria and a glucoamylase, sold for barrel-aged sours. Mild acidity, very low phenol, strong madeira and fruit notes, and a Brett character that keeps shifting across three to six months in wood. Fermentis publishes the dose and temperature but no attenuation or settling figure, so those two are typical for a mixed culture carrying an enzyme.',
    tags: [
      'sour',
      'mixed-fermentation',
      'brettanomyces',
      'barrel-aged',
      'lactobacillus',
      'enzymes',
    ],
    dosageGramsPerHl: { min: 110, max: 110 },
    provenance: 'representative',
  },
  {
    id: 'fermentis-fruit-intuition',
    lab: 'Fermentis',
    productCode: 'Fruit Intuition',
    name: 'SafSour Fruit Intuition',
    form: 'dry',
    type: 'wild',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 15, max: 25 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The same idea without the enzyme: ale yeast, Brettanomyces bruxellensis and three lactic bacteria in one sachet for mixed-fermentation sours that lean fruity rather than woody, with gentle acidity and a medium to high Brett print after a few months. Fermentis publishes no attenuation or settling figure for it, so those are typical values for a mixed culture rather than published fact.',
    tags: ['sour', 'mixed-fermentation', 'brettanomyces', 'fruity', 'lactobacillus', 'pediococcus'],
    dosageGramsPerHl: { min: 110, max: 110 },
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Lallemand (LalBrew)
  // -------------------------------------------------------------------------
  {
    id: 'lallemand-nottingham',
    lab: 'Lallemand',
    productCode: 'Nottingham',
    name: 'LalBrew Nottingham',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 77, max: 83 },
    temperatureC: { min: 10, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Vigorous, neutral English ale strain that works far colder than most ales and drops crystal clear. Its usable floor around 12 °C makes it the standard cheat for a lager-ish beer without lager tank time.',
    tags: ['english-ale', 'neutral', 'cold-tolerant', 'clearing', 'workhorse'],
    url: 'https://www.lallemandbrewing.com/en/united-states/products/nottingham-high-performance-ale-yeast/',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-windsor',
    lab: 'Lallemand',
    productCode: 'Windsor',
    name: 'LalBrew Windsor',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 68, max: 73 },
    temperatureC: { min: 15, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'Fruity English strain that stops well short of dry and leaves a full, slightly sweet body. Pair it with a low mash temperature or the beer reads cloying; it also stays hazy longer than its Nottingham sibling.',
    tags: ['english-ale', 'fruity', 'full-bodied', 'mild', 'low-attenuation'],
    url: 'https://www.lallemandbrewing.com/en/united-states/products/windsor-british-style-beer-yeast/',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-bry-97',
    lab: 'Lallemand',
    productCode: 'BRY-97',
    name: 'LalBrew BRY-97 West Coast Ale',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 15, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 13,
    sta1: false,
    description:
      'Clean West Coast ale strain that lets hop character sit unobstructed, then compacts into a very firm cake. Notoriously slow to show a lag phase — give it a full day before you decide something is wrong.',
    tags: ['west-coast', 'neutral', 'ipa', 'pale-ale', 'clearing'],
    url: 'https://www.lallemandbrewing.com/en/united-states/products/bry-97-west-coast-ale-yeast/',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-verdant-ipa',
    lab: 'Lallemand',
    productCode: 'Verdant IPA',
    name: 'LalBrew Verdant IPA',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 11,
    sta1: false,
    description:
      'English-derived strain picked for hazy IPA: soft stone-fruit esters, a rounded mouthfeel and strong biotransformation with modern hops. Handles a big dry hop without the beer thinning out.',
    tags: ['hazy', 'neipa', 'ipa', 'stone-fruit', 'english-ale'],
    url: 'https://www.lallemandbrewing.com/en/united-states/products/lalbrew-verdant-ipa/',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-new-england',
    lab: 'Lallemand',
    productCode: 'New England',
    name: 'LalBrew New England East Coast Ale',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 74, max: 82 },
    temperatureC: { min: 15, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'Ester-forward East Coast ale yeast leaning tropical and citric, built to stack flavor on top of a heavy dry hop. Leaves noticeable body and stays turbid, which is the point in a juicy IPA and a nuisance anywhere else.',
    tags: ['hazy', 'ipa', 'tropical', 'estery', 'east-coast'],
    url: 'https://www.lallemandbrewing.com/en/united-states/products/lalbrew-new-england/',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-voss-kveik',
    lab: 'Lallemand',
    productCode: 'Voss',
    name: 'LalBrew Voss Kveik',
    form: 'dry',
    type: 'kveik',
    attenuationPercent: { min: 77, max: 82 },
    temperatureC: { min: 25, max: 40 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'Norwegian farmhouse kveik that ferments happily at 35–40 °C and can finish a normal-gravity beer in two or three days. Gives a distinct orange-citrus ester with no fusel harshness despite the heat, and settles hard afterwards.',
    tags: ['kveik', 'fast', 'orange', 'high-temperature', 'farmhouse'],
    url: 'https://www.lallemandbrewing.com/en/united-states/products/lalbrew-voss-kveik-ale-yeast/',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-lutra-kveik',
    lab: 'Lallemand',
    productCode: 'Lutra',
    name: 'LalBrew Lutra Kveik',
    form: 'dry',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 20, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'The clean kveik: all of the speed and heat tolerance, almost none of the orange character. Ferments neutral enough at 30 °C to pass as a pseudo-lager, which makes it the go-to for a fast turnaround without temperature control.',
    tags: ['kveik', 'neutral', 'fast', 'pseudo-lager', 'high-temperature'],
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-diamond-lager',
    lab: 'Lallemand',
    productCode: 'Diamond Lager',
    name: 'LalBrew Diamond Lager',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 77, max: 83 },
    temperatureC: { min: 10, max: 15 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'German-derived bottom-fermenting strain that gives a clean, dry, faintly malty lager with restrained sulfur. Reliable across the whole pale lager range and quick to compact once you crash it.',
    tags: ['lager', 'pilsner', 'helles', 'clean', 'german'],
    url: 'https://www.lallemandbrewing.com/en/united-states/products/diamond-lager-yeast/',
    dosageGramsPerHl: { min: 100, max: 120 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-munich-classic',
    lab: 'Lallemand',
    productCode: 'Munich Classic',
    name: 'LalBrew Munich Classic Wheat',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'Weizen strain that delivers both halves of the profile — banana ester and clove phenol — with the balance shifting toward banana as you warm it. Leaves the beer hazy and full, as a hefeweizen should be.',
    tags: ['wheat', 'hefeweizen', 'banana', 'clove', 'phenolic'],
    url: 'https://www.lallemandbrewing.com/en/united-states/products/munich-classic-wheat-beer-yeast/',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-belle-saison',
    lab: 'Lallemand',
    productCode: 'Belle Saison',
    name: 'LalBrew Belle Saison',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 85, max: 92 },
    temperatureC: { min: 15, max: 35 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'A diastatic saison strain that will keep eating dextrins until there is nothing left, finishing near 90 percent attenuation with pepper and citrus. Genuinely STA1 positive: never assume a bottled batch has stopped, and treat shared plastic as contaminated.',
    tags: ['saison', 'diastatic', 'dry-finish', 'peppery', 'farmhouse'],
    url: 'https://www.lallemandbrewing.com/en/united-states/products/belle-saison-beer-yeast/',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-koln',
    lab: 'Lallemand',
    productCode: 'Köln',
    name: 'LalBrew Köln Kölsch-Style',
    form: 'dry',
    type: 'hybrid',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 12, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'Kölsch-style hybrid: top cropping but happy down at 13 °C, finishing crisp with a subtle white-wine fruitiness. Cold conditioning after terminal gravity is what turns it from decent to genuinely brilliant.',
    tags: ['kolsch', 'hybrid', 'clean', 'crisp', 'german'],
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-philly-sour',
    lab: 'Lallemand',
    productCode: 'Philly Sour',
    name: 'LalBrew Philly Sour',
    form: 'dry',
    type: 'wild',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 20, max: 25 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A Lachancea yeast that produces lactic acid and alcohol in the same vessel, so a sour beer needs no kettle souring step and no bacteria. Acidity builds over the first days and is strongly pitch-rate dependent — underpitch on purpose if you want it tart.',
    tags: ['sour', 'lactic', 'wild', 'non-saccharomyces', 'kettle-sour-alternative'],
    url: 'https://www.lallemandbrewing.com/en/united-states/products/wildbrew-philly-sour/',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-abbaye',
    lab: 'Lallemand',
    productCode: 'Abbaye',
    name: 'LalBrew Abbaye',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 77, max: 83 },
    temperatureC: { min: 17, max: 25 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A Belgian abbey strain covering blonde through quadrupel: tropical and banana at the warm end, raisin and fig if you ferment it cool. Spicy enough to read as Trappist, reliable enough to finish a 10% beer.',
    tags: ['belgian', 'abbey', 'tripel', 'dubbel', 'phenolic', 'high-gravity'],
    url: 'https://www.lallemandbrewing.com/en/united-states/products/abbaye-belgian-ale-yeast/',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-london',
    lab: 'Lallemand',
    productCode: 'London',
    name: 'LalBrew London',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 65, max: 72 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A true English ale that cannot eat maltotriose, so it leaves body and residual sweetness behind. Moderate esters, low flocculation and a malt-first profile that suits bitter, mild and ESB; mash cooler if you need it drier.',
    tags: ['english-ale', 'esb', 'bitter', 'mild', 'low-attenuation', 'malty'],
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-pomona',
    lab: 'Lallemand',
    productCode: 'Pomona',
    name: 'LalBrew Pomona',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A hoppy-beer hybrid bred with Escarpment: peach, citrus and tropical fruit, strong biotransformation and a stable haze. Stress-tolerant enough for high-ABV IPA, and it does not need a hot ferment to throw the fruit.',
    tags: ['hazy', 'ipa', 'peach', 'tropical', 'biotransformation', 'hybrid'],
    url: 'https://www.lallemandbrewing.com/en/united-states/products/lalbrew-pomona/',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-farmhouse',
    lab: 'Lallemand',
    productCode: 'Farmhouse',
    name: 'LalBrew Farmhouse',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 22, max: 30 },
    flocculation: 'low',
    alcoholTolerancePercent: 13,
    sta1: false,
    description:
      'A saison hybrid with the STA1 gene removed, so it finishes dry without the diastatic contamination risk of Belle Saison. Clove, pepper and tropical fruit, no hydrogen sulphide, and it will not keep eating in the bottle.',
    tags: ['saison', 'farmhouse', 'phenolic', 'non-diastatic', 'peppery'],
    url: 'https://www.lallemandbrewing.com/en/united-states/products/lalbrew-farmhouse/',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-wit',
    lab: 'Lallemand',
    productCode: 'Wit',
    name: 'LalBrew Wit',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A relatively quiet wheat strain: banana and a hint of clove, then it gets out of the way of coriander and orange peel. Lower phenol than Munich Classic, which is what a wit wants and a hefeweizen does not.',
    tags: ['witbier', 'wheat', 'belgian', 'banana', 'spicy', 'hazy'],
    url: 'https://www.lallemandbrewing.com/en/united-states/products/belgian-wit-style-ale-yeast/',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-novalager',
    lab: 'Lallemand',
    productCode: 'NovaLager',
    name: 'LalBrew NovaLager',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 77, max: 83 },
    temperatureC: { min: 10, max: 20 },
    flocculation: 'high',
    alcoholTolerancePercent: 13,
    sta1: false,
    description:
      'A bred hybrid rather than a found strain: Saccharomyces cerevisiae crossed with S. eubayanus by non-GMO methods to make a bottom-fermenting pastorianus that still works at 20 °C. It is the answer to the lager brewer with no cold room \u2014 clean, low in diacetyl, and fast enough to finish in under a week at 12 °C.',
    tags: ['lager', 'hybrid', 'warm-lager', 'clean', 'fast', 'low-diacetyl'],
    url: 'https://connect.lallemandbrewing.com/wp-content/uploads/2022/08/TDS_LPS_BREWINGYEAST_NOVALAGER_ENG_A4.pdf',
    dosageGramsPerHl: { min: 100, max: 120 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-cbc-1',
    lab: 'Lallemand',
    productCode: 'CBC-1',
    name: 'LalBrew CBC-1',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 20, max: 30 },
    flocculation: 'high',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'The conditioning strain, not a primary one: it cannot metabolise maltotriose, so it eats the priming sugar and leaves the beer\u2019s own character untouched, then settles into a mat tight enough to pour past. Rated to 12\u201314 % ABV in cask and bottle and to 18 % in cider and mead, which is what the attenuation band here describes \u2014 simple sugars, not wort. It is also a killer yeast, so keep it away from your other cultures.',
    tags: ['bottle-conditioning', 'cask', 'refermentation', 'neutral', 'cider', 'mead'],
    url: 'https://admin.lallemandbrewing.com/wp-content/uploads/2023/05/TDS_LPS_BREWINGYEAST_CBC-1_ENG_A4.pdf',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'representative',
  },
  {
    id: 'lallemand-sourvisiae',
    lab: 'Lallemand',
    productCode: 'Sourvisiae',
    name: 'Sourvisiae',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 15, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A bioengineered ale yeast carrying a single added lactate dehydrogenase gene, so it makes lactic acid and alcohol in one pass and sours a beer without ever introducing a bacterium. No acetic acid and none of the Brett or Lachancea side-flavors. Lallemand notes that apparent attenuation reads low because the lactic acid it forms is not alcohol. Being a GMO, it is not permitted everywhere \u2014 check before you brew.',
    tags: ['sour', 'kettle-sour-alternative', 'lactic', 'gmo', 'berliner-weisse', 'gose'],
    url: 'https://admin.lallemandbrewing.com/wp-content/uploads/2023/05/Sourvisiae-TDS-ENG-Print-A4-Mascoma-7.pdf',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-house-ale',
    lab: 'Lallemand',
    productCode: 'House Ale',
    name: 'LalBrew House Ale',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 78, max: 86 },
    temperatureC: { min: 16, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Selected with Escarpment Labs as a production workhorse rather than a flavor strain: very fast, POF- and diastaticus-negative, quick to clear diacetyl and acetaldehyde, and meant to be repitched. Neutral enough to sit under an IPA, a blond or a stout, and Lallemand rates it for cider and hard seltzer as well.',
    tags: ['neutral', 'workhorse', 'fast', 'american-ale', 'ipa', 'repitchable'],
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-aurora',
    lab: 'Lallemand',
    productCode: 'Aurora',
    name: 'LalBrew Aurora',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 74, max: 82 },
    temperatureC: { min: 12, max: 30 },
    flocculation: 'high',
    alcoholTolerancePercent: 13,
    sta1: false,
    description:
      'The first of the LalBrew Limited Release strains, sold for what Lallemand calls Northern IPA: floral and fruity like an East Coast yeast but finishing crisp and clean like a West Coast one, POF negative, hard flocculating, and usable anywhere from 12 to 30 °C. Being a limited release, it may not stay on the shelf.',
    tags: ['ipa', 'cold-ipa', 'floral', 'fruity', 'limited-release', 'clearing'],
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-lona',
    lab: 'Lallemand',
    productCode: 'LoNa',
    name: 'LalBrew LoNa',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 16, max: 20 },
    temperatureC: { min: 20, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A maltose- and maltotriose-negative strain bred by classical, non-GMO methods for beers under 0.5 % ABV. It stops at 16–20 % apparent attenuation, stays clean and neutral, makes no phenols and no sulfur, and cuts back the worty aldehydes that spoil most low-alcohol beer. Lallemand requires pasteurization: anything left fermentable will restart in package.',
    tags: ['non-alcoholic', 'low-alcohol', 'maltose-negative', 'neutral', 'pof-negative'],
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'lallemand-wildbrew-sour-pitch',
    lab: 'Lallemand',
    productCode: 'Sour Pitch',
    name: 'WildBrew Sour Pitch',
    form: 'dry',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 30, max: 40 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The kettle-souring Lactobacillus plantarum most breweries reach for: held at 30–40 °C it takes unhopped wort to pH 3.2–3.5 in a day or two and leaves a clean citrus tang. It produces lactic acid rather than alcohol, so a brewing yeast follows it and the attenuation figure is nominal. Hops stop it early — growth was inhibited at 4 ppm alpha acid in Lallemand tests.',
    tags: ['sour', 'lactobacillus', 'kettle-sour', 'gose', 'berliner-weisse', 'citrus'],
    dosageGramsPerHl: { min: 10, max: 10 },
    provenance: 'representative',
  },
  {
    id: 'lallemand-wildbrew-helveticus-pitch',
    lab: 'Lallemand',
    productCode: 'Helveticus Pitch',
    name: 'WildBrew Helveticus Pitch',
    form: 'dry',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 38, max: 45 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The hotter, sharper half of the WildBrew souring pair: a Lactobacillus helveticus that wants 38–45 °C and drives wort to pH 3.0–3.5 for an intense, tangy citrus sourness. Like its sibling it makes acid and not alcohol, so the attenuation figure is nominal, and 4 ppm of alpha or beta acid is enough to stall it.',
    tags: ['sour', 'lactobacillus', 'kettle-sour', 'gose', 'berliner-weisse', 'high-temperature'],
    dosageGramsPerHl: { min: 10, max: 10 },
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Mangrove Jack's (Craft Series)
  // -------------------------------------------------------------------------
  {
    id: 'mangrove-jacks-m44',
    lab: "Mangrove Jack's",
    productCode: 'M44',
    name: 'US West Coast',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Clean, high-attenuating American ale strain that keeps out of the way of hops and settles firmly. The house choice for pale ales and West Coast IPAs where the yeast should be invisible.',
    tags: ['west-coast', 'neutral', 'ipa', 'pale-ale', 'american-ale'],
    url: 'https://mangrovejacks.com/products/m44-us-west-coast-yeast-10g',
    provenance: 'representative',
  },
  {
    id: 'mangrove-jacks-m42',
    lab: "Mangrove Jack's",
    productCode: 'M42',
    name: 'New World Strong Ale',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 80, max: 86 },
    temperatureC: { min: 18, max: 28 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Built for big beers: pushes through high gravity and warm fermentation while keeping a fruity, slightly spicy profile. Suits imperial stouts, barleywines and strong dark ales where a normal ale strain would quit early.',
    tags: ['high-gravity', 'strong-ale', 'imperial-stout', 'barleywine', 'estery'],
    url: 'https://mangrovejacks.com/products/new-world-strong-ale-m42-yeast-10g',
    provenance: 'representative',
  },
  {
    id: 'mangrove-jacks-m15',
    lab: "Mangrove Jack's",
    productCode: 'M15',
    name: 'Empire Ale',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Classic English ale character — rich malt, moderate fruit esters and a soft finish rather than a dry one. A good fit for bitters, ESBs and English porters that need some sweetness left behind.',
    tags: ['english-ale', 'esb', 'bitter', 'malty', 'estery'],
    url: 'https://mangrovejacks.com/products/m15-empire-ale-yeast-10-g',
    provenance: 'representative',
  },
  {
    id: 'mangrove-jacks-m31',
    lab: "Mangrove Jack's",
    productCode: 'M31',
    name: 'Belgian Tripel',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 80, max: 86 },
    temperatureC: { min: 18, max: 28 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Strong Belgian strain that finishes dry and drops a mix of pear ester and peppery phenol on top of the alcohol. Give it warmth and a healthy pitch and it will take a tripel or golden strong right down.',
    tags: ['belgian', 'tripel', 'phenolic', 'high-gravity', 'dry-finish'],
    url: 'https://mangrovejacks.com/products/belgian-tripel-m31-yeast-10g',
    provenance: 'representative',
  },
  {
    id: 'mangrove-jacks-m36',
    lab: "Mangrove Jack's",
    productCode: 'M36',
    name: 'Liberty Bell Ale',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A transatlantic all-rounder: cleaner than an English strain, slightly more characterful than a neutral American one, with light fruit and a crisp finish. Handles most ale styles without redesigning the recipe around it.',
    tags: ['english-ale', 'american-ale', 'all-rounder', 'crisp', 'pale-ale'],
    url: 'https://mangrovejacks.com/products/liberty-bell-ale-m36-yeast-10g',
    provenance: 'representative',
  },
  {
    id: 'mangrove-jacks-m54',
    lab: "Mangrove Jack's",
    productCode: 'M54',
    name: 'Californian Lager',
    form: 'dry',
    type: 'hybrid',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 12, max: 25 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A steam-beer strain that ferments lager-clean at ale temperatures, so you get a crisp lager profile without a cold room. Best around 18 °C, where it stays clean but keeps the woody, faintly minty California common note.',
    tags: ['hybrid', 'california-common', 'steam-beer', 'lager', 'crisp'],
    url: 'https://mangrovejacks.com/products/californian-lager-m54-yeast-10g',
    provenance: 'representative',
  },
  {
    id: 'mangrove-jacks-m84',
    lab: "Mangrove Jack's",
    productCode: 'M84',
    name: 'Bohemian Lager',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 10, max: 15 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Czech-style bottom-fermenting strain that leaves a fuller, maltier body than a German lager yeast. Expect noticeable sulfur during fermentation that blows off over a few weeks of cold conditioning.',
    tags: ['lager', 'czech', 'pilsner', 'malty', 'bottom-fermenting'],
    url: 'https://mangrovejacks.com/products/m84-bohemian-lager-10g',
    provenance: 'representative',
  },
  {
    id: 'mangrove-jacks-m12',
    lab: "Mangrove Jack's",
    productCode: 'M12',
    name: 'Kveik Ale',
    form: 'dry',
    type: 'kveik',
    attenuationPercent: { min: 77, max: 84 },
    temperatureC: { min: 20, max: 40 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Voss-type kveik in a sachet: orange-citrus esters, a hard floc and a two-or-three-day finish when held hot. The Mangrove Jack pick when you have no temperature control and want beer by the weekend.',
    tags: ['kveik', 'voss', 'orange', 'fast', 'norwegian', 'farmhouse'],
    url: 'https://mangrovejacks.com/products/kveik-yeast-10g',
    provenance: 'representative',
  },
  {
    id: 'mangrove-jacks-m20',
    lab: "Mangrove Jack's",
    productCode: 'M20',
    name: 'Bavarian Wheat',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 18, max: 30 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A weizen strain throwing banana ester and clove phenol, with the balance shifting toward banana as you warm it. Stays hazy; do not fine a hefeweizen brewed with it.',
    tags: ['wheat', 'hefeweizen', 'banana', 'clove', 'phenolic', 'german'],
    url: 'https://mangrovejacks.com/products/m20-bavarian-wheat-10g',
    provenance: 'representative',
  },
  {
    id: 'mangrove-jacks-m21',
    lab: "Mangrove Jack's",
    productCode: 'M21',
    name: 'Belgian Wit',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 18, max: 25 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A witbier strain: light clove, citrus ester and a cloudy pour that coriander and orange peel can sit on. Lower banana than a weizen yeast, which is what the style wants.',
    tags: ['witbier', 'belgian', 'wheat', 'phenolic', 'spicy', 'hazy'],
    url: 'https://mangrovejacks.com/products/belgian-wit-m21-yeast-10g',
    provenance: 'representative',
  },
  {
    id: 'mangrove-jacks-m29',
    lab: "Mangrove Jack's",
    productCode: 'M29',
    name: 'French Saison',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 85, max: 90 },
    temperatureC: { min: 26, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'A diastatic French saison: pepper, citrus and a bone-dry finish, happiest at the hot end of the ale range. It will keep eating in package — confirm gravity before you bottle, and keep it off shared lines.',
    tags: ['saison', 'french', 'farmhouse', 'diastatic', 'phenolic', 'dry-finish'],
    url: 'https://mangrovejacks.com/products/m29-french-sasion-yeast-10-g',
    provenance: 'representative',
  },
  {
    id: 'mangrove-jacks-m41',
    lab: "Mangrove Jack's",
    productCode: 'M41',
    name: 'Belgian Ale',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 82, max: 88 },
    temperatureC: { min: 18, max: 28 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A high-attenuating Belgian strain for golden strongs and tripels: pear ester, peppery phenol and a dry finish. Give it warmth and a healthy pitch and it will take a big beer right down.',
    tags: ['belgian', 'tripel', 'golden-strong', 'phenolic', 'dry-finish', 'high-gravity'],
    url: 'https://mangrovejacks.com/products/belgian-ale-yeast-10g',
    provenance: 'representative',
  },
  {
    id: 'mangrove-jacks-m47',
    lab: "Mangrove Jack's",
    productCode: 'M47',
    name: 'Belgian Abbey',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 18, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'An abbey strain that leaves more body than M41, with dark fruit and a restrained clove. Better for dubbels and Belgian darks than for a bone-dry tripel.',
    tags: ['belgian', 'abbey', 'dubbel', 'malty', 'phenolic', 'esters'],
    url: 'https://mangrovejacks.com/products/belgian-abbey-m47-yeast-10g',
    provenance: 'representative',
  },
  {
    id: 'mangrove-jacks-m76',
    lab: "Mangrove Jack's",
    productCode: 'M76',
    name: 'Bavarian Lager',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 8, max: 14 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A German lager strain in the 34/70 family: clean, malt-balanced and happy from helles to bock. Fuller than the Czech M84; give it a diacetyl rest and a cold condition.',
    tags: ['lager', 'german', 'bavarian', 'helles', 'bock', 'clean'],
    url: 'https://mangrovejacks.com/products/bavarian-lager-m76-yeast-10g',
    provenance: 'representative',
  },
  {
    id: 'mangrove-jacks-m02',
    lab: "Mangrove Jack's",
    productCode: 'M02',
    name: 'Cider',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 90, max: 100 },
    temperatureC: { min: 12, max: 28 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Craft Series cider strain, working across a wide 12-28 °C band and finishing close to dry. Mangrove Jack\'s publish only the word "High" for its attenuation, so the band here is class-typical.',
    tags: ['cider', 'dry', 'apple', 'wide-range', 'flocculent'],
    url: 'https://mangrovejacks.com/products/craft-series-cider-yeast',
    provenance: 'representative',
  },
  {
    id: 'mangrove-jacks-m05',
    lab: "Mangrove Jack's",
    productCode: 'M05',
    name: 'Mead',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 15, max: 30 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A mead strain that takes a must very nearly to dryness and flocculates hard afterwards. It works from 15 °C to 30 °C, so a warm cupboard will do if a fermentation fridge is not free.',
    tags: ['mead', 'dry', 'honey', 'attenuative', 'flocculent'],
    url: 'https://mangrovejacks.com/products/mead-m05-yeast-10g',
    provenance: 'spec-sheet',
  },
  {
    id: 'mangrove-jacks-m08',
    lab: "Mangrove Jack's",
    productCode: 'M08',
    name: 'Premium Cider',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 12, max: 28 },
    flocculation: 'high',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A low-sulfur cider strain selected so that no H2S masks the fruit, finishing crisp and dry. Worth the upgrade over M02 if a previous cider came out smelling of eggs.',
    tags: ['cider', 'dry', 'low-sulphur', 'crisp', 'apple', 'flocculent'],
    url: 'https://mangrovejacks.com/products/m08-premium-cider-yeast-9g',
    provenance: 'spec-sheet',
  },
  {
    id: 'mangrove-jacks-m24',
    lab: "Mangrove Jack's",
    productCode: 'M24',
    name: 'Versa Lager',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 10, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      "A lager strain that will ferment anywhere from 10 °C to 25 °C, though Mangrove Jack's recommend keeping it under 20 °C. More attenuative than M76 or M84, so expect a drier, crisper beer.",
    tags: ['lager', 'versatile', 'wide-range', 'crisp', 'dry', 'attenuative'],
    url: 'https://mangrovejacks.com/products/m24-versa-lager-yeast-10g',
    provenance: 'spec-sheet',
  },
  {
    id: 'mangrove-jacks-m66',
    lab: "Mangrove Jack's",
    productCode: 'M66',
    name: 'Hophead Ale',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 74, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'An ale strain chosen to sit under a big hop charge without arguing with it, flocculating hard once the work is done. A dry-yeast alternative to the American liquid strains for a hop-forward pale ale.',
    tags: ['american', 'ipa', 'pale-ale', 'hop-forward', 'clean', 'flocculent'],
    url: 'https://mangrovejacks.com/products/m66-hophead-ale-yeast-10g',
    provenance: 'spec-sheet',
  },
];
